import { API_BASE } from "./config";

export const TOKEN_KEY = "srvmon-token";

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

// Header xác thực: gắn Bearer token đăng nhập (nếu có). Guest không có token.
function authHeaders() {
  const t = getToken();
  return t ? { Authorization: "Bearer " + t } : {};
}

export async function getServers() {
  // no-store: tránh trình duyệt phục vụ lại bản cũ (ETag) sau khi thêm/sửa server
  const res = await fetch(`${API_BASE}/api/servers`, { cache: "no-store" });
  if (!res.ok) throw new Error("HTTP " + res.status);
  return res.json();
}

export async function getAlerts(limit = 50) {
  const res = await fetch(`${API_BASE}/api/alerts?limit=${limit}`, {
    cache: "no-store",
  });
  if (!res.ok) throw new Error("HTTP " + res.status);
  return res.json();
}

// Cảnh báo có lọc thời gian + phân trang. Trả { rows, total, page, pageSize, totalPages }.
// Chỉ kéo đúng 1 trang từ DB (OFFSET/FETCH) thay vì đổ toàn bộ.
export async function getAlertsPaged({
  page = 1,
  pageSize = 20,
  from = null,
  to = null,
  severity = null,
  serverCode = null,
} = {}) {
  const params = new URLSearchParams({ page, pageSize });
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  if (severity) params.set("severity", severity);
  if (serverCode) params.set("server_code", serverCode);
  const res = await fetch(`${API_BASE}/api/alerts?${params.toString()}`, {
    cache: "no-store",
  });
  if (!res.ok) throw new Error("HTTP " + res.status);
  return res.json();
}

// Xoá 1 cảnh báo theo logId
export async function deleteAlert(logId) {
  const res = await fetch(`${API_BASE}/api/alerts/${logId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

// Xoá toàn bộ cảnh báo (tuỳ chọn lọc theo serverCode)
export async function deleteAllAlerts(serverCode) {
  const qs = serverCode
    ? `?server_code=${encodeURIComponent(serverCode)}`
    : "";
  const res = await fetch(`${API_BASE}/api/alerts${qs}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

export async function createServer(body) {
  const res = await fetch(`${API_BASE}/api/servers`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

export async function updateServer(code, body) {
  const res = await fetch(`${API_BASE}/api/servers/${code}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

export async function deleteServer(code) {
  const res = await fetch(`${API_BASE}/api/servers/${code}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

// Thêm app thủ công bằng PID: backend hỏi agent → lưu DB → đẩy lên dashboard.
// command (tuỳ chọn) = lệnh khởi chạy lại app khi bấm Start/Restart.
export async function lookupAppByPid(serverCode, pid, description = "", command = "") {
  const res = await fetch(
    `${API_BASE}/api/servers/${encodeURIComponent(serverCode)}/apps/lookup`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ pid, description, command }),
    }
  );
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

// Lấy mô tả + PID hiện tại của 1 app (để prefill modal Sửa)
export async function getAppInfo(serverCode, appName) {
  const res = await fetch(
    `${API_BASE}/api/servers/${encodeURIComponent(
      serverCode
    )}/apps/info?name=${encodeURIComponent(appName)}`
  );
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

// Sửa: nhập lại PID cho app đang giám sát (appName = tên hiện tại)
export async function editAppPid(serverCode, appName, pid, description = "", command = "") {
  const res = await fetch(
    `${API_BASE}/api/servers/${encodeURIComponent(serverCode)}/apps/repid`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ appName, pid, description, command }),
    }
  );
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

// Start/Stop/Restart 1 app thường (Type='process') trên server qua agent
export async function appAction(serverCode, appName, action) {
  const res = await fetch(
    `${API_BASE}/api/servers/${encodeURIComponent(serverCode)}/apps/action`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ appName, action }),
    }
  );
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

// Xoá app khỏi giám sát
export async function deleteApp(serverCode, appName) {
  const res = await fetch(
    `${API_BASE}/api/servers/${encodeURIComponent(serverCode)}/apps`,
    {
      method: "DELETE",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ appName }),
    }
  );
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

export async function getPm2() {
  const res = await fetch(`${API_BASE}/api/pm2`);
  if (!res.ok) throw new Error("HTTP " + res.status);
  return res.json();
}

export async function getPm2Logs(serverCode, name, lines = 300) {
  const res = await fetch(
    `${API_BASE}/api/pm2/${encodeURIComponent(serverCode)}/${encodeURIComponent(
      name
    )}/logs?lines=${lines}`
  );
  if (!res.ok) throw new Error("HTTP " + res.status);
  return res.json();
}

export async function pm2Action(serverCode, name, action) {
  const res = await fetch(
    `${API_BASE}/api/pm2/${encodeURIComponent(serverCode)}/${encodeURIComponent(
      name
    )}/${action}`,
    { method: "POST", headers: authHeaders() }
  );
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

// ── Triển khai (Deploy) ───────────────────────────────────────────────────────

// Cấu hình deploy đã lưu của 1 app (trên tblServers_App) — để prefill khi re-deploy.
export async function getAppDeployInfo(serverCode, appName) {
  const res = await fetch(
    `${API_BASE}/api/servers/${encodeURIComponent(serverCode)}/apps/${encodeURIComponent(
      appName
    )}/deploy`,
    { headers: authHeaders(), cache: "no-store" }
  );
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

// Khởi động lại app đã deploy (chạy lại lệnh đã lưu trong thư mục — không cần upload).
export async function startDeployedApp(serverCode, appName) {
  const res = await fetch(
    `${API_BASE}/api/servers/${encodeURIComponent(serverCode)}/apps/${encodeURIComponent(
      appName
    )}/start`,
    { method: "POST", headers: authHeaders() }
  );
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

// Danh sách app đã deploy (có FolderPathNode) trên server.
export async function getDeployedApps(serverCode) {
  const res = await fetch(
    `${API_BASE}/api/servers/${encodeURIComponent(serverCode)}/deployed-apps`,
    { headers: authHeaders(), cache: "no-store" }
  );
  if (!res.ok) throw new Error("HTTP " + res.status);
  return res.json();
}

export async function getDeployHistory(serverCode, limit = 20) {
  const res = await fetch(
    `${API_BASE}/api/servers/${encodeURIComponent(
      serverCode
    )}/deploy-history?limit=${limit}`,
    { headers: authHeaders(), cache: "no-store" }
  );
  if (!res.ok) throw new Error("HTTP " + res.status);
  return res.json();
}

// Đẩy thư mục: files = FileList (từ input webkitdirectory). overrides tuỳ chọn.
export async function deployFolder(serverCode, files, overrides = {}) {
  const fd = new FormData();
  for (const f of files) {
    fd.append("files", f);
    fd.append("paths", f.webkitRelativePath || f.name);
  }
  Object.entries(overrides).forEach(([k, v]) => {
    if (v != null) fd.append(k, v);
  });
  const res = await fetch(
    `${API_BASE}/api/servers/${encodeURIComponent(serverCode)}/deploy`,
    { method: "POST", headers: authHeaders(), body: fd }
  );
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

// ── Remote & lệnh chức năng ───────────────────────────────────────────────────

function postCmd(serverCode, path, body) {
  return fetch(
    `${API_BASE}/api/servers/${encodeURIComponent(serverCode)}/${path}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(body || {}),
    }
  ).then(async (res) => ({ ok: res.ok, status: res.status, data: await res.json().catch(() => ({})) }));
}

// Lưu/cập nhật user đăng nhập remote của 1 server vào tblUsers
export function saveServerLogin(serverCode, user, password, displayName) {
  return postCmd(serverCode, "login", { user, password, displayName });
}

// Danh sách tài khoản remote đã lưu của server (admin)
export async function getServerUsers(serverCode) {
  const res = await fetch(
    `${API_BASE}/api/servers/${encodeURIComponent(serverCode)}/users`,
    { headers: authHeaders() }
  );
  if (!res.ok) throw new Error("HTTP " + res.status);
  return res.json();
}

// Chạy lệnh trên agent → trả { out, error, exitCode }. shell: 'powershell' | 'cmd'.
export function runShell(serverCode, command, shell = "powershell") {
  return postCmd(serverCode, "shell", { command, shell });
}

export function serverRestart(serverCode, { user, password, reason } = {}) {
  return postCmd(serverCode, "restart", { user, password, reason });
}

export function serverShutdown(serverCode, { user, password } = {}) {
  return postCmd(serverCode, "shutdown", { user, password });
}

// Đổi mật khẩu tài khoản Windows trên server qua agent (net user) + lưu lại
export function changeServerPassword(serverCode, { user, oldPassword, newPassword, adminPassword } = {}) {
  return postCmd(serverCode, "passwd", { user, oldPassword, newPassword, adminPassword });
}

// ── Xác thực & quản lý tài khoản đăng nhập ────────────────────────────────────

export async function login(username, password) {
  const res = await fetch(`${API_BASE}/api/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

export async function getMe() {
  const res = await fetch(`${API_BASE}/api/auth/me`, { headers: authHeaders() });
  if (!res.ok) throw new Error("HTTP " + res.status);
  return res.json();
}

export async function getUsers() {
  const res = await fetch(`${API_BASE}/api/auth/users`, { headers: authHeaders() });
  if (!res.ok) throw new Error("HTTP " + res.status);
  return res.json();
}

export async function createUser(body) {
  const res = await fetch(`${API_BASE}/api/auth/users`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

export async function updateUser(id, body) {
  const res = await fetch(`${API_BASE}/api/auth/users/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

export async function deleteUser(id) {
  const res = await fetch(`${API_BASE}/api/auth/users/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}
